import { useSubjectMember } from '@healthblocks-io/core'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { FlexColumn, Margin, Padding } from '@/@bothrs-ui/web/components/layout'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { P } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { globalDefinitions } from '@/defined-translations'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { mixpanel } from '@/mixpanel'
import { KeyValue } from '@/modules/shared/components/key-value/KeyValue'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { useToast } from '@/modules/shared/hooks/useToast'
import { ContactInformationForm } from '../../components/contact-information-form/ContactInformationForm'

type ContactInformationPageProps = {
  title: string
  description?: string
  actionLabel?: string
  onComplete?: () => void
}

const ContactInformationPage = ({ title, description }: ContactInformationPageProps) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const member = useSubjectMember()
  const { showSuccessToast } = useToast()

  useEffect(() => {
    mixpanel.track('onboarding_personal_information_submit')
  }, [])

  // Interaction methods
  // ------------------------------------------------------------------------- /
  function handleInfoUpdated() {
    mixpanel.track('profile_updated')
    showSuccessToast({
      message: t({
        id: 'profile.account-information.toast',
        comment: 'Account info updated toast message',
        message: 'Account informatie aangepast',
      }),
    })

    navigate('/profile', { replace: true })
  }

  // Render Methods
  // ------------------------------------------------------------------------- /
  return (
    <ContentPageLayout
      noScroll
      header={
        <AppHeader
          primaryAction={{
            label: i18n._(globalDefinitions.goBack),
            icon: ['far', 'chevron-left'],
            onClick: () => {
              navigate('/profile', { replace: true })
            },
          }}
        />
      }
    >
      <Padding pb={theme.UI.Spacing.Space10}>
        <FlexColumn alignItems='space-between' gap={theme.UI.Spacing.Space4}>
          <H3 noMargin>{title}</H3>
          {description ? <P type='BodyMediumRegular'>{description}</P> : null}

          <FlexColumn gap={theme.UI.Spacing.Space4}>
            <FlexColumn gap={theme.UI.Spacing.Space2}>
              <KeyValue label={i18n._(globalDefinitions.labelName)} value={member?.name} />
            </FlexColumn>
            <Line color={theme.Colors.Neutrals.Lighter} />
            {member?.birthDate ? (
              <>
                <FlexColumn gap={theme.UI.Spacing.Space2}>
                  <KeyValue
                    label={i18n._(globalDefinitions.labelDateOfBirth)}
                    value={formatLocale(member?.birthDate, 'dd/MM/yyyy')}
                  />
                </FlexColumn>
                <Line color={theme.Colors.Neutrals.Lighter} />
              </>
            ) : null}
            {member?.gender ? (
              <>
                <FlexColumn gap={theme.UI.Spacing.Space2}>
                  <KeyValue
                    label={i18n._(globalDefinitions.gender)}
                    value={i18n._(
                      globalDefinitions[`gender${member.gender}` as keyof typeof globalDefinitions] as string,
                    )}
                  />
                </FlexColumn>
                <Line color={theme.Colors.Neutrals.Lighter} />
              </>
            ) : null}
          </FlexColumn>

          <Margin mt={theme.UI.Spacing.Space10}>
            <ContactInformationForm
              actionLabel={t({
                id: 'profile.contactinfo.save',
                message: 'Aanpassingen opslaan',
              })}
              onComplete={handleInfoUpdated}
            />
          </Margin>
        </FlexColumn>
      </Padding>
    </ContentPageLayout>
  )
}

export { ContactInformationPage }
